import Input from "../../components/Input/Input"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import Button from "../../components/Button/Button"
import { NavLink, useNavigate } from "react-router-dom"
import { areFieldsNotEmpty } from "../../helpers/helper"
import { phoneNumber } from "../../utils/payment"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import ContainerPage from "../../components/ContainerPage/ContainerPage"
import PageTitle from "../../components/PageTitle/PageTitle"
import Calendar from "../../components/Calendar/Calendar"
import { StorageGetItem, USER } from "../../lib/storage"
import { createNewAffiliate } from "../../features/affiliates/affiliates"
import { clearListCache } from "../../utils/listCache"
import { fetchContactGroupsDropdownInfo } from "../../features/contactGroups/contactGroups"
import Dropdown from "../../components/Dropdown/Dropdown"
import { dateToServer, isRealDate } from "../../utils/date"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { AFFILIATE_STAFF_SCREEN_FIXES } from "../../config/featureFlagKeys"

const AddAffiliates = () => {
  const dispatch = useDispatch()
  const requireRealBirthDate = useSelector(selectFeatureFlag(AFFILIATE_STAFF_SCREEN_FIXES))
  const { triggerLoading } = useMyContextLoading()
  const [reqError, setReqError] = useState('')
  const [contactGroups, setContactGroups] = useState([])
  const [userRole, setUserRole] = useState('')
  const affiliateManagerOptions = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' }
  ]
  const accessLevelOptions = [
    { value: 'limited', label: 'Limited (Group Only)' },
    { value: 'fullContactsNoAccounting', label: 'Full Contacts' }
  ]
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    primaryPhone: '',
    address: {
      address: '',
      aptNumber: '',
      city: '',
      state: '',
      zip: ''
    },
    dateOfBirth: '',
    contactGroups: [],
    affiliateManager: null,
    accessLevel: 'limited'
  })
  const [isFormValid, setIsFormValid] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
      setFormData(prev => ({
        ...prev,
        company: result?.company || '',
      }))
    })
  }, [])

  useEffect(() => {
    const handleReqError = () => {
      reqError.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }

    handleReqError()
  }, [reqError])

  const handleCreateAffiliates = async () => {
    let newNumber = formData?.primaryPhone
    triggerLoading(true)
    if (newNumber) {
      newNumber = formData.primaryPhone?.replace(/\D/g, "")
    }

    const response = await dispatch(createNewAffiliate({
      userData: {
        ...formData,
        primaryPhone: newNumber,
        dateOfBirth: dateToServer(formData.dateOfBirth),
      }
    }))
    if (response.error) {
      showSnackBar("error", response?.payload || "Something went wrong")
      triggerLoading(false)
    } else if (response) {
      showSnackBar("success", "Your changes are saved")
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        primaryPhone: '',
        address: {
          address: '',
          aptNumber: '',
          city: '',
          state: '',
          zip: ''
        },
        dateOfBirth: '',
        contactGroups: [],
        affiliateManager: null,
        accessLevel: 'limited'
      }
      )
      triggerLoading(false)
      clearListCache('affiliates')
      navigate("/affiliates")
    }
  }

  const getContactGroupsDropdownInfo = async () => {
    triggerLoading(true)
    const response = await dispatch(fetchContactGroupsDropdownInfo())
    if (response.error) {
      showSnackBar("error", response?.payload || "Something went wrong")
    } else {
      setContactGroups(response?.payload?.data?.filter(group => group.isDepartment !== true))
    }
    triggerLoading(false)
  }

  const handleInputChange = ({ name, value }) => {
    switch (name) {
      case 'primaryPhone':
        setFormData((prevData) => ({ ...prevData, [name]: phoneNumber(value) }))
        break
      default:
        const [parentField, childField] = name.split('.')
        if (childField) {
          setFormData((prevData) => ({
            ...prevData,
            [parentField]: {
              ...prevData[parentField],
              [childField]: value
            }
          }))
        } else {
          setFormData((prevData) => ({
            ...prevData,
            [name]: value
          }))
        }
        break
    }
  }

  useEffect(() => {
    const checkFormValidity = () => {
      setIsFormValid(
        areFieldsNotEmpty([formData.firstName, formData.lastName, formData.email]) &&
        (requireRealBirthDate ? isRealDate(formData.dateOfBirth) : formData.dateOfBirth !== "")
      )
    }

    checkFormValidity()
  }, [formData, requireRealBirthDate])

  useEffect(() => {
    getContactGroupsDropdownInfo()
  }, [])

  return (
    <ContainerPage>
      <div className="flex justify-between items-center">
        <PageTitle title="Create affiliate" backTo="/affiliates" />
      </div>
      <div className="flex w-full items-center justify-center">
        <div className="w-full max-w-[850px] rounded-2xl bg-surface border border-divider shadow-card p-12">
          <div className="grid grid-cols-2 gap-x-4 gap-y-6 pb-6 h-max border-b border-divider ">
            <Input
              width="w-full"
              placeholder="Enter First Name"
              label="First Name *"
              value={formData?.firstName}
              onChange={(e) => handleInputChange({ name: "firstName", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter Last Name"
              label="Last Name *"
              value={formData?.lastName}
              onChange={(e) => handleInputChange({ name: "lastName", value: e.target.value })}
            />
            <Input
              type="email"
              width="w-full"
              placeholder="Enter Email"
              label="Email *"
              value={formData?.email}
              onChange={(e) => handleInputChange({ name: "email", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter Primary Phone"
              label="Primary Phone"
              value={formData?.primaryPhone}
              onChange={(e) => handleInputChange({ name: "primaryPhone", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter Address"
              label="Address"
              value={formData?.address?.address}
              onChange={(e) => handleInputChange({ name: "address.address", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="34"
              label="Suite/Apt#"
              value={formData?.address?.aptNumber}
              onChange={(e) => handleInputChange({ name: "address.aptNumber", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter City"
              label="City"
              value={formData?.address?.city}
              onChange={(e) => handleInputChange({ name: "address.city", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter State"
              label="State"
              value={formData?.address?.state}
              onChange={(e) => handleInputChange({ name: "address.state", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter Zip"
              label="Zip"
              value={formData?.address?.zip}
              onChange={(e) => handleInputChange({ name: "address.zip", value: e.target.value })}
            />
            <Calendar
              width="w-full"
              label="Date Of Birth *"
              height="56px"
              optional
              value={formData?.dateOfBirth}
              onChange={(e) => handleInputChange({ name: "dateOfBirth", value: e })}
            />
            <Dropdown
              value={formData?.contactGroups || []}
              setValue={(e) => handleInputChange({ name: "contactGroups", value: e })}
              menuItems={contactGroups}
              isMulti
              className="w-full mt-2"
              variant="standard"
              placeholder="Group"
              label="Contact Groups"
            />
            {userRole === 'customer' && (
              <Dropdown
                value={formData?.affiliateManager}
                setValue={(e) => handleInputChange({ name: "affiliateManager", value: e })}
                menuItems={affiliateManagerOptions}
                className="w-full mt-2"
                variant="standard"
                placeholder="Select option"
                label="Senior Affiliate"
              />
            )}
            {userRole === 'customer' && (
              <Dropdown
                value={formData?.accessLevel}
                setValue={(e) => handleInputChange({ name: "accessLevel", value: e })}
                menuItems={accessLevelOptions}
                className="w-full mt-2"
                variant="standard"
                placeholder="Select access level"
                label="Access Level"
              />
            )}
          </div>
          <div className={`mt-6 flex justify-end space-x-4`}>
            <NavLink to={"/affiliates"}>
              <Button classes={"mr-4"} type="outlined" size="lg" width="min-w-[156px]" onClick={() => {
              }}>Close</Button>
            </NavLink>
            <Button
              onClick={handleCreateAffiliates}
              size="lg"
              width="min-w-[156px]"
              disabled={!isFormValid}
              classes="rounded-lg"
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </ContainerPage>
  )
}

export default AddAffiliates
